import React, { useEffect, useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import ProductsPanel from "./addItems/ProductsPanel";
import ServicesPanel from "./addItems/ServicesPanel";
import AutoSearchAddForm from "./addItems/AutoSearchAddForm";
import GiftSeriesPanel from "./addItems/GiftSeriesPanel";

/* ---------------- Add Items dialog (Products / Services / Gift Card·Series) ----------------
   Thin shell that wires the Tabs surface to the individual per-tab
   panels. Every panel keeps its own local filter/search state so
   switching tabs never clobbers what the operator was typing in
   another tab. The `onAdd` callback bubbles up unchanged so the
   parent BranchOrders page keeps receiving the exact same order-line
   payloads (kind, ref_id, name, quantity, unit_price, discount, meta).
*/
export default function AddItemsDialog({ open, onOpenChange, products, services, series, giftTemplates, packages, branch, staffList = [], appointmentDate = null, onAdd }) {
  const [tab, setTab] = useState("products");

  useEffect(() => {
    if (!open) return;
    setTab("products");
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl w-[calc(100vw-1rem)] max-h-[85vh] p-0 flex flex-col overflow-hidden sm:w-full"
        data-testid="add-items-dialog"
      >
        <DialogHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-2 shrink-0 border-b border-border">
          <DialogTitle>Add Items</DialogTitle>
          <DialogDescription>Search or filter, set quantity, then add to the current order.</DialogDescription>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 sm:px-6 py-3" data-testid="add-items-scroll">
          <Tabs value={tab} onValueChange={setTab}>
            <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
              <TabsList className="bg-transparent p-0 border-b border-border rounded-none w-max min-w-full justify-start gap-1 h-auto">
                <TabsTrigger value="products" data-testid="tab-products" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2">Products</TabsTrigger>
                <TabsTrigger value="services" data-testid="tab-services" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2">Services</TabsTrigger>
                <TabsTrigger value="packages" data-testid="tab-packages" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2">Packages</TabsTrigger>
                <TabsTrigger value="series" data-testid="tab-series" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2">Gift Card / Series</TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="products" className="mt-4">
              <ProductsPanel products={products} branch={branch} onAdd={onAdd} />
            </TabsContent>

            <TabsContent value="services" className="mt-4">
              <ServicesPanel services={services} branch={branch} staffList={staffList} appointmentDate={appointmentDate} onAdd={onAdd} />
            </TabsContent>

            <TabsContent value="packages" className="mt-4">
              <AutoSearchAddForm
                items={packages}
                branch={branch}
                placeholder="Type a package name…"
                emptyHint="Start typing to search packages created in Brand → Packages."
                onAdd={(row, qty) => onAdd({ kind: "package", ref_id: row.id, name: `${row.name} (Package)`, quantity: qty, unit_price: row.price || 0, discount: 0 })}
                testidPrefix="add-package"
              />
            </TabsContent>

            <TabsContent value="series" className="mt-4">
              <GiftSeriesPanel
                series={series}
                giftTemplates={giftTemplates}
                packages={packages}
                services={services}
                branch={branch}
                onAdd={onAdd}
              />
            </TabsContent>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}
